<template>
  <sp-icon :name="iconName" :size="size" class="sp-caret" :class="classModifiers" :fill-color="color"></sp-icon>
</template>

<script setup>
import { computed } from "vue";
import { toBoolean } from "#ce/utils/props";
import { props as caretProps } from "./props.js";

const props = defineProps(caretProps);

const iconNames = {
  outlined: "chevron-down",
  filled: "triangle-down-filled",
  double: "select-double-caret-filled",
  chevron: "chevron-down-filled",
};

const iconName = computed(() => iconNames[props.variant]);

const classModifiers = computed(() => {
  const modifiers = {
    "--is-active": toBoolean(props.active),
    "--transition-rotate": toBoolean(props.transitionRotate),
    [`--variant-${props.variant}`]: true,
  };

  // Expand mode and static direction are mutually exclusive: emitting both
  // `--expand-*` and `--dir-*` would let their equal-specificity transforms
  // fight by source order. Pick one based on whether `expandDirection` is set.
  if (props.expandDirection) {
    modifiers[`--expand-${props.expandDirection}`] = true;
  } else {
    modifiers[`--dir-${props.direction}`] = props.direction;
  }

  return modifiers;
});
</script>

<style>
/*
 * Two size levers, mirroring sp-icon's own model:
 *   • the `size` prop (design-system token) — passed through to sp-icon, takes
 *     precedence because it sets --size on sp-icon's inner element.
 *   • `--sp-comp-caret-size` (continuous length) — feeds sp-icon's
 *     --sp-comp-icon-size below for passed-through / computed sizing (e.g. a
 *     row's icon size).
 * Either way the glyph scales uniformly in both dimensions, rather than the host
 * clamping only one axis.
 */
:host {
  display: inline-block;
  /* Optical nudge for inline use. A context that box-centres the caret zeroes it. */
  transform: translateY(var(--sp-comp-caret-offset, -0.125rem));
}
</style>

<style lang="scss" scoped>
.sp-caret {
  // Feed the public --sp-comp-caret-size token into sp-icon's size token. The
  // default lives in the var() fallback (not as a :host declaration) so a
  // theme/ancestor-level --sp-comp-caret-size override can cascade through. The
  // `size` prop, when set, wins by setting --size on sp-icon's inner element.
  --sp-comp-icon-size: var(--sp-comp-caret-size, var(--sp-sys-icon-size-medium, 2rem));

  // `transitionRotate` only toggles whether the rotation animates — it must not
  // gate the rotation itself. The active/direction transforms below are keyed on
  // `--is-active` / `--dir-*` independently, so the caret rotates correctly even
  // when `transitionRotate=false` (only the animation is suppressed).
  &.--transition-rotate {
    transition: transform 0.2s ease-in-out;
  }

  // Resting orientation by pointing direction (default mode).
  &.--dir-down {
    transform: rotate(0);
  }

  &.--dir-up {
    transform: rotate(-180deg);
  }

  &.--dir-left {
    transform: rotate(90deg);
  }

  &.--dir-right {
    transform: rotate(-90deg);
  }

  // The double (stacked up/down) glyph never rotates. Declared after the
  // direction rules so it wins over the equal-specificity `--dir-*` resting
  // rotation.
  &.--variant-double {
    transform: none;
  }

  // Active flip. Declared after the resting rules so the equal-specificity
  // `--is-active` flip overrides the `--dir-*` resting rotation. The translateY
  // is an optical nudge for inline contexts; it runs inside the rotated
  // coordinate system, so it shifts the flipped glyph visually downward.
  // Contexts that box-centre the caret (e.g. list rows) zero it via
  // --sp-comp-caret-active-nudge.
  &.--is-active {
    transform: rotate(-180deg) translateY(var(--sp-comp-caret-active-nudge, calc(var(--sp-ref-spacing-2) * -1)));
  }

  &.--is-active.--variant-filled {
    transform: rotate(-180deg);
  }

  &.--is-active.--variant-double {
    transform: none;
  }

  // Expand mode: resting points right ("▶"), active points down ("▼"). The
  // higher-specificity active rule overrides the default 180° `--is-active`
  // flip above.
  &.--expand-down {
    transform: rotate(-90deg);
  }

  &.--expand-down.--is-active {
    transform: rotate(0deg);
  }
}
</style>
